import React, { useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import Dh from '../components/Dh';
import Navbar2 from '../component/navbar2';
import Footer from '../components/BrandFooter';
import { L7_MEDIA } from '@/data/landingMedia';
import L7Reveal from '@/components/landing/L7Reveal';
import { L7PhotoBand } from '@/components/landing/L7Sections';

// ── Design tokens ─────────────────────────────────────────────────────────────
const Y   = '#FFD54F';
const YD  = '#E6B400';
const YS  = '#FFF6DC';
const CR  = '#FCFDFF';
const PA  = '#F1F0E4';
const INK = '#17263A';
const I2  = '#42505F';
const MU  = '#6E7888';
const LN  = '#E5E8EA';
const WH  = '#ffffff';
const GR  = '#15824E';
const YT  = '#96700A';  // accent text/links — the brand yellow taken dark enough to read

// ── Pricing constants ─────────────────────────────────────────────────────────
type Fees  = { vatReg: number; vatFiling: number; ctReg: number; ctFiling: number };
const DFLT_FEES: Fees  ={ vatReg: 500, vatFiling: 1_000, ctReg: 500, ctFiling: 2_000 };
const DFLT_BANDS: Record<string, number> = {
  'Up to 100': 1_500, '100 – 200': 2_000, '200 – 500': 3_000, '500+': 5_000,
};
const BAND_KEYS = ['Up to 100', '100 – 200', '200 – 500', '500+'] as const;
type BandKey = typeof BAND_KEYS[number];
const BAND_SLUG: Record<string, string> = {
  'Up to 100': 'upto100', '100 – 200': '100-200', '200 – 500': '200-500', '500+': '500plus',
};
const SVC = ['Bookkeeping', 'VAT Registration', 'VAT Filing', 'Corporate Tax Registration', 'Corporate Tax Filing'];

// ── Subscription plans (admin-editable via pricing/current.packages) ────────────
type Pkg = { name: string; tag: string; desc: string; inherit: string; features: string[]; priceYr: number; priceMo: number | null; discount: string | null; trial: string | null; govFees: boolean; popular: boolean };
const DFLT_PACKAGES: Pkg[] = [
  { name: 'Pulse', tag: '(for Existing Businesses)', desc: 'For founders who already have a registered business and want to stay compliant, investor-ready, and growth-focused with the right legal and financial tools.', inherit: 'Includes:', features: ['Business Health & Investor Readiness Report', 'Legal & Compliance Check (Trade License, MOA, VAT)', 'Corporate Tax Registration & Advisory', 'Access to Business Templates (NDAs, MOA, Resolutions)'], priceYr: 1100, priceMo: null, discount: null, trial: '30-Day Free Trial', govFees: true, popular: false },
  { name: 'Total Compliance', tag: '', desc: 'For entrepreneurs who want to stay 100% compliant with UAE regulations.', inherit: 'Everything in Pulse +', features: ['VAT registration + quarterly VAT filings', 'Accounting & bookkeeping dashboard (cash flow, P&L, balance sheet)', 'UAE Corporate Tax (CT) registration & annual return filing (FTA)', 'Includes up to 100 bookkeeping transactions per month', 'Automated transaction integrations (Stripe, Shopify, Lean, etc.)', 'Annual financial statements (CFO-ready)'], priceYr: 14399, priceMo: 1499, discount: '20% OFF', trial: null, govFees: true, popular: false },
  { name: 'Business-in-a-Box™', tag: '', desc: 'For entrepreneurs who want full legal, accounting, and tax compliance, plus a dedicated accountant.', inherit: 'Everything in Total Compliance +', features: ['Corporate Tax (CT) registration & annual return filing (FTA) + quarterly tax planning & estimates', 'Includes up to 200 bookkeeping transactions per month', 'Quarterly compliance advisory session', 'Investor-ready reporting (deck financials, data room prep)', 'Priority support'], priceYr: 22999, priceMo: 2399, discount: '20% OFF', trial: null, govFees: true, popular: true },
];
const planSlug = (s: string) => s.replace(/™/g, '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// ── Featured packages ─────────────────────────────────────────────────────────
const FEAT = [
  { key: 'full',      flags: [1,1,1,1,1], name: 'Full',        pop: true,  desc: 'Bookkeeping, VAT and Corporate Tax: fully managed.',              feats: ['Monthly bookkeeping', 'VAT registration + filing', 'CT registration + filing', 'One dashboard for everything'] },
  { key: 'books-vat', flags: [1,1,1,0,0], name: 'Books + VAT', pop: false, desc: 'Bookkeeping with VAT registration and quarterly filing.',          feats: ['Monthly bookkeeping', 'VAT registration', 'Quarterly VAT filing'] },
  { key: 'books-ct',  flags: [1,0,0,1,1], name: 'Books + CT',  pop: false, desc: 'Bookkeeping with Corporate Tax registration and annual filing.',   feats: ['Monthly bookkeeping', 'CT registration', 'Annual CT filing'] },
  { key: 'tax-only',  flags: [0,1,1,1,1], name: 'Tax only',    pop: false, desc: 'VAT and Corporate Tax handled, no bookkeeping.',                  feats: ['VAT registration + filing', 'CT registration + filing', 'No bookkeeping required'] },
];

// ── Price calculation ─────────────────────────────────────────────────────────
function calcPrice(flags: number[], fees: Fees, bands: Record<string, number>, band: string) {
  let once = 0, month = 0, qtr = 0, year = 0;
  if (flags[0]) month += bands[band] ?? 0;
  if (flags[1]) once  += fees.vatReg;
  if (flags[2]) qtr   += fees.vatFiling;
  if (flags[3]) once  += fees.ctReg;
  if (flags[4]) year  += fees.ctFiling;
  let hv = 0, hu = '', k = '';
  if (month > 0)     { hv = month; hu = '/ month';        k = 'm'; }
  else if (once > 0) { hv = once;  hu = 'one-time setup'; k = 'o'; }
  else if (qtr > 0)  { hv = qtr;   hu = '/ quarter';      k = 'q'; }
  else if (year > 0) { hv = year;  hu = '/ year';         k = 'y'; }
  return { once, month, qtr, year, hv, hu, k };
}

// ── Date helpers ──────────────────────────────────────────────────────────────
function nextVatDeadline(): Date {
  const today = new Date();
  for (let y = today.getFullYear(); y < today.getFullYear() + 2; y++) {
    for (const m of [2, 5, 8, 11]) {       // Mar/Jun/Sep/Dec quarter-ends (0-based)
      const qEnd = new Date(y, m + 1, 0);  // last day of that month
      const dl = new Date(qEnd);
      dl.setDate(dl.getDate() + 28);
      if (dl >= today) return dl;
    }
  }
  return new Date();
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d);
  const day = r.getDate();
  r.setDate(1);
  r.setMonth(r.getMonth() + n);
  r.setDate(Math.min(day, new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate()));
  return r;
}

const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// ── Shared mini-components ────────────────────────────────────────────────────
function FadeIn({ children, delay = 0, className = '', style }: { children: ReactNode; delay?: number; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(es => {
      if (es[0]?.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={style}>
      {children}
    </div>
  );
}

const Tick = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M5 12l5 5L20 7" stroke={GR} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Eyebrow = ({ children }: { children: ReactNode }) => (
  <span style={{ fontWeight: 800, fontSize: 12.5, letterSpacing: '0.16em', textTransform: 'uppercase' as const, background: Y, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, color: INK }}>
    {children}
  </span>
);

// ══════════════════════════════════════════════════════════════════════════════
export default function AccountingPricing() {
  const [fees,    setFees]    = useState<Fees>(DFLT_FEES);
  const [bands,   setBands]   = useState<Record<string, number>>(DFLT_BANDS);
  /* Accounting is quoted in dirhams only — no currency state, no conversion. */
  const cur = 'AED';
  const [bFlags,  setBFlags]  = useState<number[]>([1, 1, 1, 1, 1]);
  const [bBand,   setBBand]   = useState<BandKey>('100 – 200');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [packages, setPackages] = useState<Pkg[]>(DFLT_PACKAGES);
  const [bill,    setBill]    = useState<'mo' | 'yr'>('mo');

  useEffect(() => {
    const ref = doc(db, 'pricing', 'current');
    return onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.vatReg) setFees({ vatReg: d.vatReg, vatFiling: d.vatFiling, ctReg: d.ctReg, ctFiling: d.ctFiling });
      if (d.bands)  setBands(d.bands);
      if (Array.isArray(d.packages) && d.packages.length) setPackages(d.packages);
    }, err => { if (err.name !== 'AbortError') console.warn('Pricing snapshot error:', err); });
  }, []);

  // Checker state
  const [cVat,  setCVat]  = useState<'yes' | 'no' | null>(null);
  const [cTurn, setCTurn] = useState<'lo' | 'mid' | 'hi' | null>(null);
  const [cFye,  setCFye]  = useState('');
  const [cBook, setCBook] = useState<BandKey | null>(null);

  // Currency helpers
  const money = (aed: number) => {
    const val = Math.round(aed).toLocaleString('en-US');
    return `AED ${val}`;
  };
  const Money = ({ aed }: { aed: number }) => {
    const val = Math.round(aed).toLocaleString('en-US');
    return <><Dh/>{val}</>;
  };
  const subItems = (p: ReturnType<typeof calcPrice>) => {
    const s: string[] = [];
    if (p.once  > 0 && p.k !== 'o') s.push(`${money(p.once)} setup`);
    if (p.month > 0 && p.k !== 'm') s.push(`${money(p.month)} /mo`);
    if (p.qtr   > 0 && p.k !== 'q') s.push(`${money(p.qtr)} /qtr`);
    if (p.year  > 0 && p.k !== 'y') s.push(`${money(p.year)} /yr`);
    return s;
  };
  const href = (plan: string, flags: number[], band: string) =>
    `/accounting/signup?service=accounting&plan=${plan}&flags=${flags.join('')}&band=${BAND_SLUG[band] ?? 'upto100'}&cur=${cur}`;

  // Smooth-scroll to an in-page section (guarantees the jump works regardless of router/scroll container)
  const scrollToId = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Checker computations ───────────────────────────────────────────────────
  const checkerAny = cVat !== null || cTurn !== null || cFye !== '' || cBook !== null;
  const mustReg    = cVat === 'no' && cTurn === 'hi';

  const vatBadge = (() => {
    if (!cVat) return null;
    if (cVat === 'yes') {
      const dl = nextVatDeadline();
      return { text: `Registered. File & pay within 28 days of each quarter end. Next deadline: ${fmtDate(dl)}.`, kind: 'ok' as const, label: 'Registered ✓' };
    }
    if (!cTurn) return { text: 'Pick your turnover to see if registration is required.', kind: null, label: null };
    if (cTurn === 'hi')  return { text: 'Turnover above AED 375,000: VAT registration is mandatory.', kind: 'warn' as const, label: 'Action needed' };
    if (cTurn === 'mid') return { text: 'Within the voluntary band (AED 187,500–375,000): you may register voluntarily.', kind: 'info' as const, label: 'Optional' };
    return { text: 'Below the AED 187,500 threshold: registration isn\'t required yet.', kind: 'ok' as const, label: 'No action needed' };
  })();

  const ctText = cFye
    ? `Return & payment due by ${fmtDate(addMonths(new Date(cFye), 9))} (9 months after year end). FTA registration required.`
    : 'Pick your financial year end to see your CT deadline.';

  const checkerFlags = [1, mustReg ? 1 : 0, (cVat === 'yes' || mustReg) ? 1 : 0, 1, 1];

  // ── Build-your-own ────────────────────────────────────────────────────────
  const byoP  = calcPrice(bFlags, fees, bands, bBand);
  const byoSub = subItems(byoP);
  const byoName = bFlags.every(f => f === 0) ? 'Select a service' : SVC.filter((_, i) => bFlags[i]).join(' + ');
  const toggleFlag = (i: number) => {
    const next = [...bFlags];
    next[i] = next[i] ? 0 : 1;
    setBFlags(next);
  };

  const FAQS = [
    { q: 'How does billing work?', a: 'Registrations are one-time, bookkeeping is billed monthly by your transaction volume, VAT filing is quarterly and Corporate Tax filing is annual. You only pay for the services in your package.' },
    { q: 'What if my transaction volume changes?', a: 'Your bookkeeping fee follows your volume band. Move up or down a band and your monthly price adjusts, no penalty.' },
    { q: 'Can I pick just one service?', a: 'Yes. Use "Build your own" to choose any combination, from a single VAT registration up to the full managed package.' },
    { q: 'Who actually files my returns?', a: 'Filing on your behalf is handled by a registered FTA tax agent, so your VAT and Corporate Tax submissions are compliant and on time.' },
    { q: 'Can I cancel or change my package?', a: "Yes, you can change or cancel anytime from your dashboard. There's no setup fee." },
  ];

  const badgeStyle = (kind: 'ok' | 'warn' | 'info' | null): React.CSSProperties =>
    kind === 'warn' ? { background: '#5b1f1f', color: '#ffb4a8' }
    : kind === 'info' ? { background: '#3a3414', color: '#ffe38a' }
    : { background: '#16432c', color: '#8fe3b6' };

  return (
    <div style={{ background: CR, color: INK, fontFamily: "'Montserrat', system-ui, sans-serif", lineHeight: 1.55, overflowX: 'clip' }}>
      <style>{`
        .ac-seg { border: 1.5px solid ${LN}; background: ${WH}; font-family: inherit; font-weight: 700; font-size: 13px; padding: 9px 14px; border-radius: 9px; cursor: pointer; color: ${I2}; transition: all .15s; }
        .ac-seg.on { background: ${INK}; color: #fff; border-color: ${INK}; }
        .ac-tier { transition: transform .25s, box-shadow .25s; }
        .ac-tier:hover { transform: translateY(-6px); box-shadow: 0 26px 60px -24px rgba(23,38,58,.30); }
        .ac-btn:hover { transform: translateY(-3px); }
        .ac-row:hover td { background: rgba(23,38,58,.022); }
        .ac-row:hover td.ac-pop { background: #FFF0B0; }
        @media (max-width: 820px) { .ac-split { grid-template-columns: 1fr !important; } }
        @supports (grid-template-rows: subgrid) {
          @media (min-width: 768px) {
            .ac-tiers { grid-template-rows: repeat(7, auto) 1fr; }
            .ac-tiers > *, .ac-tiers > * > .ac-tier { grid-row: 1 / -1; display: grid; grid-template-rows: subgrid; }
          }
        }
      `}</style>

      <Navbar2 signupTo="/accounting/signup" />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{ position: 'relative', background: `radial-gradient(120% 90% at 82% -10%, ${YS} 0%, ${CR} 55%)`, overflow: 'hidden', padding: '74px 0 24px' }}>
        <div className="max-w-[820px] mx-auto px-6" style={{ position: 'relative', zIndex: 2 }}>
          <FadeIn><Eyebrow>Accounting &amp; Tax · UAE</Eyebrow></FadeIn>
          <FadeIn delay={80}>
            <h1 style={{ fontSize: 'clamp(40px,5.4vw,66px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.04, marginTop: 18 }}>
              Stay compliant. Know your cost in{' '}
              <span style={{ position: 'relative', whiteSpace: 'nowrap' }}>
                <span aria-hidden style={{ position: 'absolute', left: -2, right: -2, bottom: 6, height: '0.32em', background: Y, zIndex: -1, transform: 'skewX(-4deg)', display: 'block' }} />
                60 seconds.
              </span>
            </h1>
          </FadeIn>
          <FadeIn delay={160}>
            <p style={{ color: I2, fontSize: 19, marginTop: 22, maxWidth: 640 }}>
              UAE Corporate Tax and VAT deadlines are real, and missing them is costly. Answer a few questions, see your next deadlines and your price instantly, then let registered FTA tax agents handle it.
            </p>
          </FadeIn>
          <FadeIn delay={240}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 28 }}>
              <a href="#checker" onClick={e => scrollToId(e, 'checker')} className="ac-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${YD}`, background: Y, color: INK, textDecoration: 'none', transition: 'transform .16s, box-shadow .22s' }}>
                Get my free check →
              </a>
              <a href="#plans" onClick={e => scrollToId(e, 'plans')} className="ac-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${INK}`, background: 'transparent', color: INK, textDecoration: 'none', transition: 'transform .16s, background .2s, color .2s' }}>
                See plans
              </a>
            </div>
          </FadeIn>
          <FadeIn delay={320}>
            <p style={{ marginTop: 22, color: MU, fontSize: 14 }}>Free check · no signup to start · filed by registered FTA tax agents · cancel anytime</p>
          </FadeIn>
        </div>
      </section>

      {/* ── UAE band (real photography) ─────────────────────── */}
      <div className="l7">
        <L7Reveal />
        <L7PhotoBand
          src={L7_MEDIA.dubaiBusinessDistrict}
          alt="Dubai's business district"
          title="Priced for the UAE, filed with the FTA."
          lead="Every plan below is quoted in dirhams, with government fees passed through at cost, never marked up."
          tone="plain"
        />
      </div>

      {/* ── Subscription plans ────────────────────────────────────────────── */}
      <section id="plans" style={{ padding: '72px 0 40px' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center" style={{ maxWidth: 760, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '16px 0 12px' }}>Choose a plan to power up your business</h2>
            <p style={{ color: I2, fontSize: 17, maxWidth: 620, margin: '0 auto' }}>
              For businesses that are already registered. Fixed annual fees: government fees are charged at cost, never marked up. Prices in <strong>{cur}</strong>.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap', marginTop: 20 }}>
              <div style={{ display: 'inline-flex', border: `1.5px solid ${LN}`, borderRadius: 999, padding: 4, background: WH }}>
                <button className="ac-btn" onClick={() => setBill('mo')} style={{ border: 'none', background: bill === 'mo' ? INK : 'transparent', color: bill === 'mo' ? '#fff' : MU, fontFamily: 'inherit', fontWeight: 800, fontSize: 13, padding: '8px 18px', borderRadius: 999, cursor: 'pointer' }}>Monthly</button>
                <button className="ac-btn" onClick={() => setBill('yr')} style={{ border: 'none', background: bill === 'yr' ? INK : 'transparent', color: bill === 'yr' ? '#fff' : MU, fontFamily: 'inherit', fontWeight: 800, fontSize: 13, padding: '8px 18px', borderRadius: 999, cursor: 'pointer' }}>Annual <span style={{ color: bill === 'yr' ? Y : GR, fontSize: 11, marginLeft: 5 }}>save 20%</span></button>
              </div>
            </div>
          </FadeIn>

          <div className="ac-tiers grid grid-cols-1 md:grid-cols-3 gap-5 mt-10 items-stretch">
            {packages.map((p, i) => {
              const annualOnly = p.priceMo == null;
              const big  = bill === 'mo' && !annualOnly ? p.priceMo! : p.priceYr;
              const unit = bill === 'mo' && !annualOnly ? '/ month' : '/ year';
              const sub  = annualOnly
                ? (p.govFees ? '+ government fees, charged at cost' : 'Annual plan only')
                : bill === 'yr'
                  ? `Works out at ${money(Math.round(p.priceYr / 12))}/month · you save ${money(p.priceMo! * 12 - p.priceYr)} vs monthly`
                  : `${money(p.priceMo! * 12)} per year${p.govFees ? ' · + government fees' : ''}`;
              const showOff = bill === 'yr' && !annualOnly && p.discount;
              return (
                <FadeIn key={p.name + i} delay={i * 90} className="h-full">
                  <div className="ac-tier h-full flex flex-col" style={{ background: WH, border: `${p.popular ? 2 : 1.5}px solid ${p.popular ? INK : LN}`, borderRadius: 22, padding: '30px 26px', position: 'relative', boxShadow: p.popular ? '0 26px 60px -24px rgba(23,38,58,.30)' : undefined }}>
                    {p.popular && <span style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: INK, color: Y, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', borderRadius: 999, padding: '5px 14px', whiteSpace: 'nowrap' }}>Most Popular</span>}
                    {showOff && <span style={{ position: 'absolute', top: 22, right: 22, background: '#E4F3EB', color: GR, fontSize: 10, fontWeight: 800, borderRadius: 999, padding: '4px 9px' }}>{p.discount}</span>}
                    <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{p.name}{p.tag && <small style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: MU, marginTop: 3 }}>{p.tag}</small>}</h3>
                    <p style={{ fontSize: 13.5, color: I2, lineHeight: 1.55, margin: '10px 0 0', minHeight: 76 }}>{p.desc}</p>
                    <div style={{ paddingTop: 18, marginTop: 4, borderTop: `1px solid ${LN}` }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, flexWrap: 'wrap' }}>
                        <b style={{ fontSize: 38, fontWeight: 900, letterSpacing: '-0.04em' }}><Money aed={big} /></b>
                        <span style={{ fontSize: 14, fontWeight: 800, color: MU }}>{unit}</span>
                        {showOff && <span style={{ fontSize: 10, fontWeight: 800, color: GR, background: '#E4F3EB', borderRadius: 999, padding: '3px 8px' }}>SAVE 20%</span>}
                      </div>
                      <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: MU, marginTop: 7, minHeight: 30, lineHeight: 1.4 }}>{sub}</span>
                    </div>
                    <a href={`/accounting/signup?service=accounting&plan=${planSlug(p.name)}&billing=${bill}&cur=${cur}`} className="ac-btn"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 6, padding: '12px 18px', borderRadius: 999, fontWeight: 700, fontSize: 14.5, border: `2px solid ${p.popular ? INK : YD}`, background: p.popular ? INK : Y, color: p.popular ? CR : INK, textDecoration: 'none', transition: 'transform .16s' }}>
                      Choose {p.name.replace('™', '')}
                    </a>
                    <span style={{ display: 'block', textAlign: 'center', fontSize: 11.5, fontWeight: 800, color: GR, marginTop: 9, minHeight: 17 }}>{p.trial || ''}</span>
                    <div style={{ height: 1, background: LN, margin: '14px 0' }} />
                    <div style={{ fontSize: 11.5, fontWeight: 800, color: INK, marginBottom: 10 }}>{p.inherit}</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                      {p.features.map((f, j) => (
                        <li key={j} style={{ display: 'flex', gap: 9, fontSize: 13.5, fontWeight: 600, alignItems: 'flex-start', color: I2 }}><Tick size={16} />{f}</li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>
              );
            })}
          </div>
          <p style={{ textAlign: 'center', marginTop: 22, color: MU, fontSize: 14 }}>
            Only need one or two services?{' '}
            <a href="#build" onClick={e => scrollToId(e, 'build')} style={{ color: YT, fontWeight: 700 }}>Build your exact price below →</a>
          </p>
        </div>
      </section>

      {/* ── The free check, in a photograph ───────────────────────────────── */}
      <div className="l7">
        <L7PhotoBand
          src={L7_MEDIA.adviserMeeting}
          alt="An adviser going through a client's position with them at a laptop"
          title="Four questions here. A real accountant behind the answer."
          lead="The check below is instant. What follows it is a person who reads your position and tells you where you actually stand."
          tone="plain"
          align="right"
        />
      </div>

      {/* ── Compliance Checker ────────────────────────────────────────────── */}
      <section id="checker" style={{ padding: '64px 0 40px' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center">
            <Eyebrow>Free check · 60 seconds</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '16px 0 12px' }}>
              Are you compliant? What will it cost?
            </h2>
            <p style={{ color: I2, fontSize: 17, maxWidth: 640, margin: '0 auto' }}>
              Answer four quick questions. See your next VAT &amp; Corporate Tax deadlines and an instant price estimate, no signup needed.
            </p>
          </FadeIn>

          <FadeIn>
            <div className="ac-split" style={{ display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', background: WH, border: `1px solid ${LN}`, borderRadius: 24, boxShadow: '0 26px 60px -24px rgba(23,38,58,.30)', overflow: 'hidden', marginTop: 28 }}>

              {/* Left — form */}
              <div style={{ padding: '32px 36px' }}>
                <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 4 }}>Tell us about your business</h3>
                <p style={{ color: MU, fontSize: 14, marginBottom: 22 }}>UAE-based. Nothing is stored until you choose to sign up.</p>

                {/* Q1 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontWeight: 800, fontSize: 13, color: I2, marginBottom: 8 }}>Are you registered for VAT?</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(['yes', 'no'] as const).map(v => (
                      <button key={v} className={`ac-seg ${cVat === v ? 'on' : ''}`} onClick={() => setCVat(v)}>
                        {v === 'yes' ? 'Yes' : 'Not yet'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Q2 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontWeight: 800, fontSize: 13, color: I2, marginBottom: 8 }}>Annual turnover (AED)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {([['lo', 'Under 187,500'], ['mid', '187,500 – 375,000'], ['hi', 'Over 375,000']] as const).map(([v, label]) => (
                      <button key={v} className={`ac-seg ${cTurn === v ? 'on' : ''}`} onClick={() => setCTurn(v)}>{label}</button>
                    ))}
                  </div>
                </div>

                {/* Q3 */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontWeight: 800, fontSize: 13, color: I2, marginBottom: 8 }}>Financial year end</label>
                  <input type="date" value={cFye} onChange={e => setCFye(e.target.value)}
                    style={{ width: '100%', border: `1.5px solid ${LN}`, borderRadius: 9, padding: '10px 12px', fontFamily: 'inherit', fontSize: 14, color: INK, outline: 'none' }} />
                </div>

                {/* Q4 */}
                <div>
                  <label style={{ display: 'block', fontWeight: 800, fontSize: 13, color: I2, marginBottom: 8 }}>Monthly transactions (bookkeeping)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {BAND_KEYS.map(v => (
                      <button key={v} className={`ac-seg ${cBook === v ? 'on' : ''}`} onClick={() => setCBook(v)}>{v}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right — results */}
              <div style={{ background: 'linear-gradient(160deg,#1c1d12,#2a2410)', color: WH, padding: '32px 36px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: Y, fontWeight: 800 }}>Your snapshot</div>
                <h4 style={{ fontSize: 20, color: WH, margin: '4px 0 18px', fontWeight: 800 }}>Your compliance &amp; cost</h4>

                {!checkerAny ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {[
                      { icon: <><path d="M12 8v4l3 2"/><circle cx="12" cy="12" r="9"/></>, title: 'VAT', body: 'Select your VAT status to see your next filing deadline.' },
                      { icon: <><path d="M3 9h18M3 5h18v14H3z"/></>, title: 'Corporate Tax return', body: 'Pick your financial year end to see your CT deadline.' },
                      { icon: <><path d="M4 19V5m4 14V9m4 10V7m4 12v-6m4 6V4"/></>, title: 'Bookkeeping', body: 'Pick your monthly volume to get an instant price.' },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 0', borderTop: '1px solid rgba(255,255,255,.1)', opacity: 0.45 }}>
                        <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: 'rgba(255,213,79,.16)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth={2}>{row.icon}</svg>
                        </div>
                        <div style={{ fontSize: 13, color: '#cfc9b4' }}>
                          <b style={{ color: WH, fontSize: 14, display: 'block', marginBottom: 2 }}>{row.title}</b>
                          {row.body}
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: 18, background: 'rgba(255,213,79,.12)', border: '1px dashed rgba(255,213,79,.35)', borderRadius: 11, padding: '13px 16px', fontSize: 13, color: '#a59d82', textAlign: 'center' }}>
                      Answer the questions and your deadlines + price appear here instantly.
                    </div>
                  </div>
                ) : (
                  <>
                    {/* VAT row */}
                    {[
                      {
                        icon: <><path d="M12 8v4l3 2"/><circle cx="12" cy="12" r="9"/></>,
                        title: 'VAT',
                        body: vatBadge ? vatBadge.text : 'Let us know if you\'re VAT registered.',
                        badge: vatBadge?.kind ?? null,
                        badgeLabel: vatBadge?.label ?? null,
                      },
                      {
                        icon: <><path d="M3 9h18M3 5h18v14H3z"/></>,
                        title: 'Corporate Tax return',
                        body: ctText,
                        badge: null,
                        badgeLabel: null,
                      },
                      {
                        icon: <><path d="M4 19V5m4 14V9m4 10V7m4 12v-6m4 6V4"/></>,
                        title: 'Bookkeeping',
                        body: cBook ? `${cBook} transactions / month` : 'Pick your monthly volume for a price.',
                        badge: null,
                        badgeLabel: null,
                      },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 0', borderTop: '1px solid rgba(255,255,255,.1)' }}>
                        <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: 'rgba(255,213,79,.16)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth={2}>{row.icon}</svg>
                        </div>
                        <div style={{ fontSize: 13, color: '#cfc9b4' }}>
                          <b style={{ color: WH, fontSize: 14, display: 'block', marginBottom: 2 }}>{row.title}</b>
                          {row.body}
                          {row.badge && row.badgeLabel && (
                            <span style={{ display: 'inline-block', marginTop: 4, fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 999, ...badgeStyle(row.badge) }}>
                              {row.badgeLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Price + CTA */}
                    <div style={{ marginTop: 'auto', paddingTop: 18 }}>
                      {cBook ? (
                        <>
                          <div style={{ fontSize: 30, fontWeight: 900, color: WH, lineHeight: 1 }}>{money(bands[cBook] ?? 0)}</div>
                          <div style={{ fontSize: 13, color: '#cfc9b4', fontWeight: 700 }}>/ month · bookkeeping</div>
                          <div style={{ fontSize: 12, color: '#a59d82', marginTop: 4 }}>
                            plus VAT filing {money(fees.vatFiling)}/qtr · CT filing {money(fees.ctFiling)}/yr
                            {mustReg && ` · VAT reg ${money(fees.vatReg)} one-time`}
                            {` · CT reg ${money(fees.ctReg)} one-time`}
                          </div>
                          <a href={href('custom', checkerFlags, cBook)}
                            style={{ display: 'block', marginTop: 16, background: Y, color: INK, fontFamily: 'inherit', fontWeight: 800, fontSize: 15, padding: '13px 20px', borderRadius: 11, textAlign: 'center', textDecoration: 'none', cursor: 'pointer', transition: 'transform .15s' }}>
                            Get this handled →
                          </a>
                        </>
                      ) : (
                        <a href="#plans" onClick={e => scrollToId(e, 'plans')}
                          style={{ display: 'block', background: Y, color: INK, fontFamily: 'inherit', fontWeight: 800, fontSize: 15, padding: '13px 20px', borderRadius: 11, textAlign: 'center', textDecoration: 'none', cursor: 'pointer' }}>
                          See plans →
                        </a>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Build Your Own ────────────────────────────────────────────────── */}
      <section id="build" style={{ background: PA, padding: '80px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center">
            <Eyebrow>Build your own</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '16px 0 12px' }}>Any combination, priced instantly</h2>
            <p style={{ color: I2, fontSize: 17, maxWidth: 580, margin: '0 auto' }}>
              Select the exact services and your bookkeeping volume: see your price, then sign up.
            </p>
          </FadeIn>

          <FadeIn style={{ marginTop: 36 }}>
            <div className="ac-split" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', background: WH, border: `1px solid ${LN}`, borderRadius: 24, boxShadow: '0 26px 60px -24px rgba(23,38,58,.30)', overflow: 'hidden' }}>
              {/* Left */}
              <div style={{ padding: '32px 36px' }}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MU, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 10 }}>Services</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {SVC.map((name, i) => {
                      const on = bFlags[i] === 1;
                      return (
                        <label key={name} onClick={() => toggleFlag(i)} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: on ? INK : '#B6BCC4', cursor: 'pointer', userSelect: 'none' }}>
                          <div style={{ width: 18, height: 18, border: `2px solid ${on ? INK : LN}`, borderRadius: 5, background: on ? INK : WH, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .15s' }}>
                            {on && <svg width={10} height={10} viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke={WH} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </div>
                          {name}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MU, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 8 }}>Bookkeeping volume</label>
                  <select value={bBand} onChange={e => setBBand(e.target.value as BandKey)} disabled={!bFlags[0]}
                    style={{ width: '100%', border: `1.5px solid ${LN}`, borderRadius: 12, padding: '13px 14px', fontSize: 15, fontWeight: 700, color: INK, fontFamily: 'inherit', background: !bFlags[0] ? '#F4F6F9' : WH, cursor: !bFlags[0] ? 'not-allowed' : 'pointer' }}>
                    {BAND_KEYS.map(k => <option key={k} value={k}>{k} txns / month</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: MU, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 8 }}>Currency</label>
                </div>
              </div>

              {/* Right */}
              <div style={{ background: INK, color: CR, padding: '32px 36px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: 11.5, textTransform: 'uppercase' as const, letterSpacing: '0.07em', color: '#9fb0c9', fontWeight: 800 }}>Your price</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: WH, margin: '4px 0 14px', lineHeight: 1.3 }}>{byoName}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 38, fontWeight: 900, color: WH, letterSpacing: '-0.03em' }}>{byoP.hv ? <Money aed={byoP.hv} /> : '-'}</span>
                  <span style={{ fontSize: 13, color: '#9fb0c9', fontWeight: 700 }}>{byoP.hu}</span>
                </div>
                <div style={{ color: '#AEB9D6', fontSize: 13, fontWeight: 600, marginTop: 5 }}>
                  {byoSub.length ? `plus ${byoSub.join(' · ')}` : 'no other fees'}
                </div>
                <div style={{ margin: '16px 0', borderTop: '1px solid #233056', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[
                    byoP.once  > 0 && ['One-time setup', `${money(byoP.once)}`],
                    byoP.month > 0 && ['Bookkeeping',    `${money(byoP.month)} /mo`],
                    byoP.qtr   > 0 && ['VAT filing',     `${money(byoP.qtr)} /qtr`],
                    byoP.year  > 0 && ['CT filing',      `${money(byoP.year)} /yr`],
                  ].filter(Boolean).map((row: any) => (
                    <div key={row[0]} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#cdd8ea', padding: '3px 0' }}>
                      <span>{row[0]}</span>
                      <b style={{ color: WH }}>{row[1]}</b>
                    </div>
                  ))}
                </div>
                <a href={href('custom', bFlags, bBand)} className="ac-btn"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 'auto', gap: 9, padding: '13px 20px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${YD}`, background: Y, color: INK, textDecoration: 'none', transition: 'transform .16s', cursor: 'pointer' }}>
                  Get started →
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── What the price actually buys ──────────────────────────────────── */}
      <div className="l7">
        <L7PhotoBand
          src={L7_MEDIA.filingPlanner}
          alt="A calendar with pins marking the days that run up to a circled date"
          title="What you're buying is a year that never surprises you."
          lead="Whatever you tick above becomes dates on our calendar: VAT quarters, the Corporate Tax return, and the reminders that come before each one."
          tone="plain"
          align="right"
        />
      </div>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section id="faq" style={{ padding: '80px 0' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn className="text-center">
            <Eyebrow>Pricing FAQ</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,48px)', fontWeight: 800, letterSpacing: '-0.025em', margin: '16px 0 0' }}>Questions about pricing</h2>
          </FadeIn>
          <FadeIn>
            <div style={{ maxWidth: 800, margin: '28px auto 0' }}>
              {FAQS.map((faq, i) => (
                <div key={i} style={{ borderBottom: `1px solid ${LN}` }}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '20px 4px', fontSize: 17, fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, color: INK }}>
                    {faq.q}
                    <span style={{ fontSize: 25, transition: 'transform .3s', color: YD, transform: openFaq === i ? 'rotate(45deg)' : 'none', flexShrink: 0 }}>+</span>
                  </button>
                  <div style={{ maxHeight: openFaq === i ? 280 : 0, overflow: 'hidden', transition: 'max-height .32s ease', color: I2, fontSize: 15.5 }}>
                    <p style={{ padding: '0 4px 22px' }}>{faq.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section style={{ padding: '0 0 104px' }}>
        <div className="max-w-[1160px] mx-auto px-6">
          <FadeIn>
            <div style={{ background: INK, borderRadius: 30, padding: '64px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(${Y} .9px, transparent .9px)`, backgroundSize: '24px 24px', opacity: 0.10, pointerEvents: 'none' }} />
              <h2 style={{ fontSize: 'clamp(32px,4.6vw,52px)', fontWeight: 800, letterSpacing: '-0.025em', color: WH, position: 'relative' }}>
                Get your accounting handled.
              </h2>
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,.9)', maxWidth: 540, margin: '14px auto 26px', position: 'relative' }}>
                Choose a plan, sign up in minutes, and hand off your books, VAT and Corporate Tax.
              </p>
              <a href="#plans" onClick={e => scrollToId(e, 'plans')} className="ac-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, border: `2px solid ${YD}`, background: Y, color: INK, textDecoration: 'none', position: 'relative', transition: 'transform .16s, box-shadow .22s', cursor: 'pointer' }}>
                Choose your plan →
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
